import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import { buildLoggerStreamUrl } from '@app/modules/logger/screens/stream/helpers/build-logger-stream-url';
import { useStreamQualityReportCapture } from '../../hooks/useStreamQualityReportCapture';
import React, { useCallback, useState } from 'react';
import { TimelineTooltipPayload } from '../../lib/timeline-tooltip-types';
import { useReportViewStore } from '../../store/view.store';
import { MinimumStation, Station } from '@app/core/types/stations';

type StationsFinder = ReturnType<typeof useStationsFinder>;

/**
 * Row hover (bold label), portaled timeline tooltip, and “open logger at event start” for chart bars.
 */
export function useTimelineChartInteractions() {
  const { openLogger } = useStreamQualityReportCapture();
  const [hoveredStationId, setHoveredStationId] = useState<string | null>(null);
  const finder = useStationsFinder();
  const showTimelineTooltipAtClientPoint = useReportViewStore(
    (s) => s.showTimelineTooltipAtClientPoint,
  );
  const hideTimelineTooltip = useReportViewStore((s) => s.hideTimelineTooltip);

  const showHover = useCallback(
    (event: React.MouseEvent<SVGRectElement>, data: TimelineTooltipPayload) => {
      showTimelineTooltipAtClientPoint({ clientX: event.clientX, clientY: event.clientY }, data);
    },
    [showTimelineTooltipAtClientPoint],
  );

  const onStationRowMouseLeave = useCallback(() => {
    setHoveredStationId(null);
    hideTimelineTooltip();
  }, [hideTimelineTooltip]);

  const openIssueInLogger = useCallback(
    (payload: TimelineTooltipPayload) => {
      openLogger({ source: 'timeline', payload });
      const station = resolveStationForLogger(finder, payload.stationId);
      const url = buildLoggerStreamUrl(station.slug, {
        cursor: payload.start.getTime(),
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [finder, openLogger],
  );

  return {
    hoveredStationId,
    setHoveredStationId,
    finder,
    showHover,
    hideTimelineTooltip,
    onStationRowMouseLeave,
    openIssueInLogger,
  };
}

function resolveStationForLogger(
  finder: StationsFinder,
  stationId: string,
): Station | MinimumStation {
  return finder(stationId) ?? { id: stationId, name: stationId, slug: stationId };
}
